import React from 'react';

/*
 * Page head and page shell.
 *
 * Measured from the rendered platform: eyebrow 11px semibold uppercase in
 * #777982 with 7px beneath it, H1 32px bold in #18191D, sub 14px in #666971.
 * The dashboard H1 is 32px, not the 56px marketing H1: that difference is what
 * separates a screen that reads as an operator tool from one that reads as a
 * landing page. All of it is IBM Plex Sans; mono is for values, not labels.
 */

export interface PageHeadProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  sub?: React.ReactNode;
  actions?: React.ReactNode;
  /*
   * Where the controls sit against the heading block. They align to its
   * baseline by default, which is right when the sub is one line. A head with
   * a taller block centres them instead, so a lone button does not hang off
   * the bottom of three lines of text.
   */
  align?: 'end' | 'center';
  className?: string;
}

const ALIGN: Record<string, string> = {
  end: 'items-end',
  center: 'items-center',
};

export const PageHead: React.FC<PageHeadProps> = ({
  eyebrow, title, sub, actions, align = 'end', className = '',
}) => (
  <div className={`mb-8 flex flex-wrap justify-between gap-6 ${ALIGN[align]} ${className}`}>
    {/* The 260px basis sends the actions under the title on a phone rather
        than letting them sit on top of it. */}
    <div className="flex min-w-0 flex-1 basis-[260px] flex-col">
      {eyebrow && (
        <p className="m-0 mb-[7px] text-[11px] font-semibold uppercase leading-[16.3px] tracking-[0.14em] text-eyebrow">
          {eyebrow}
        </p>
      )}
      <h1 className="m-0 text-[26px] font-bold leading-[33.2px] tracking-[-0.015em] text-ink sm:text-[32px] sm:leading-[40.4px]">{title}</h1>
      {sub && <p className="mb-0 mt-2.5 text-[14px] leading-[23px] text-ink-muted">{sub}</p>}
    </div>
    {actions && <div className="flex max-w-full flex-wrap items-center gap-2">{actions}</div>}
  </div>
);

/*
 * The page container.
 *
 * It carries no gutter of its own — the app shell already applies the 30px one
 * around `children` — but it does carry the measure. The audit's content frame
 * is 1198px wide with equal insets at the reference viewport, which is a
 * centred max-width rather than padding: 30px sides and a 1200px cap
 * reproduce both the wide capture and the narrow one from a single rule.
 *
 * The cap is the reason a row is readable on a 27-inch display. Without it a
 * title, its description, and its status chip end up a third of a metre apart
 * and the eye stops connecting them.
 */
export const PageBody: React.FC<{ className?: string; children: React.ReactNode }> = ({ className = '', children }) => (
  <div className={`mx-auto w-full max-w-[1200px] ${className}`}>{children}</div>
);
